import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Checkbox,
  Chip,
  LinearProgress,
} from "@mui/material";
import { IoLocationOutline } from "react-icons/io5";
import { useSelector } from "react-redux";
import { selectCyclePhase } from "../../redux/slices/powerCycleSlice";

const statusColors = {
  ONLINE: "success.main",
  OFFLINE: "error.main",
};

const phaseLabels = {
  initiated: "Cycling...",
  power_off: "Power off",
  device_offline: "Offline",
  complete: "Complete!",
};

const DeviceCard = ({ device, isSelected, onSelect, onClick }) => {
  const color = statusColors[device.status] || statusColors.OFFLINE;
  const cyclePhase = useSelector(selectCyclePhase(device.serial_number));
  const isCycling = !!cyclePhase && cyclePhase !== "complete";

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: "pointer",
        border: isSelected ? 2 : 1,
        borderColor: isCycling
          ? "warning.main"
          : isSelected
          ? "primary.main"
          : "divider",
        transition: "all 0.2s",
        "&:hover": { boxShadow: 6, transform: "translateY(-2px)" },
        height: "100%",
        position: "relative",
        overflow: "visible",
      }}
    >
      {isCycling && (
        <LinearProgress
          color="warning"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            borderRadius: "4px 4px 0 0",
          }}
        />
      )}
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Checkbox
            checked={isSelected}
            onChange={onSelect}
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{ p: 0, mr: 1 }}
          />
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: isCycling ? "warning.main" : color,
              flexShrink: 0,
              mt: 0.5,
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.3 },
              },
              ...(isCycling && {
                animation: "pulse 1.5s ease-in-out infinite",
              }),
            }}
          />
        </Box>
        <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }} noWrap>
          {device.name || device.serial_number}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <IoLocationOutline /> {device.location || "No location"}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          SN: {device.serial_number}
        </Typography>
        {device.firmware_version && (
          <Typography variant="caption" color="text.secondary" display="block">
            FW: {device.firmware_version}
          </Typography>
        )}
        {cyclePhase ? (
          <Chip
            label={phaseLabels[cyclePhase] || cyclePhase}
            size="small"
            color={cyclePhase === "complete" ? "success" : "warning"}
            sx={{ mt: 1 }}
          />
        ) : (
          device.status && (
            <Chip
              label={device.status}
              size="small"
              color={device.status === "ONLINE" ? "success" : "error"}
              sx={{ mt: 1 }}
            />
          )
        )}
      </CardContent>
    </Card>
  );
};

export default DeviceCard;
